import { createMachine, assign, raise } from "xstate";
import { Messages } from "./scanview.types";
import { GlobalLogger } from "./debugLogger";

const scanViewStateMachine = createMachine<ScanViewContext, ScanViewEvent>(
  {
    id: "scanStatus",
    initial: "cameraLoading",
    predictableActionArguments: true,
    context: {
      card_id: "",
      scan_id: "",
      autoScan: true,
      scannerRunning: false,
      backSideSupport: false,
      eligibilitySupport: false,
      headerTitle: "Hold Card in Frame\nIt Will Scan Automatically",
      messageState: {},
      toggledAutoManual: false,
      processing: false,
      httpCode: 0,
      httpMessage: "",
      session_id: "",
      modelState: "",
    },
    states: {
      cameraLoading: {
        on: {
          CAMERA_LOADED: {
            target: "autoCapture",
            cond: (context) => context.modelState === "warmed",
            actions: [
              assign({
                scannerRunning: true,
              }),
            ],
          },
          UPDATE_MODEL_STATE: {
            actions: [
              assign({
                modelState: (context, event) => event.modelState,
              }),
            ],
          },
          ERROR: {
            target: "error",
            actions: [
              assign({
                scannerRunning: true,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
          CAMERAERROR: {
            target: "cameraError",
            actions: [
              assign({
                scannerRunning: true,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
          eligibilityError: {
            target: "eligibilityError",
          },
        },
      },
      autoCapture: {
        on: {
          "card.frontside_processing": {
            target: "frontsideProcessing",
            actions: [
              assign({
                card_id: (context, event) => event.card_id,
                session_id: (context, event) => event.session_id,
                scan_id: (context, event) => event.scan_id,
                backSideSupport: (context, event) =>
                  event.backSideSupport ? event.backSideSupport : false,
                eligibilitySupport: (context, event) =>
                  event.eligibilitySupport,
                processing: true,
              }),
            ],
          },
          TOGGLE: {
            target: "manualCapture",
            actions: [
              assign({
                autoScan: false,
                scannerRunning: false,
                toggledAutoManual: true,
              }),
            ],
          },
          "card.error": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          ERROR: {
            target: "error",
            actions: [
              assign({
                scannerRunning: true,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
        },
      },
      manualCapture: {
        on: {
          "card.frontside_processing": {
            target: "frontsideProcessing",
            actions: [
              assign({
                scannerRunning: true,
                processing: true,
              }),
            ],
          },
          MANUAL_BUTTON_CLICK: {
            target: "frontsideProcessing",
            actions: [
              assign({
                scannerRunning: true,
                processing: true,
              }),
            ],
          },
          TOGGLE: {
            target: "autoCapture",
            actions: [
              assign({
                autoScan: true,
                scannerRunning: true,
                toggledAutoManual: true,
              }),
            ],
          },
          "card.error": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          ERROR: {
            target: "error",
            actions: [
              assign({
                scannerRunning: true,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
        },
      },
      frontsideProcessing: {
        on: {
          //when frontside only
          "card.completed": [
            {
              target: "completed",
              actions: [
                assign({
                  scannerRunning: false,
                }),
              ],
            },
          ],
          //when backside supported
          "card.backside_processing": [
            {
              target: "backsidePending",
              actions: [
                assign({
                  scannerRunning: false,
                  processing: false,
                }),
              ],
            },
          ],
          "card.post_processing": [
            {
              target: "postProcessing",
              actions: [
                assign({
                  scannerRunning: false,
                  processing: true,
                }),
              ],
            },
          ],
          "card.frontside_failed": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          "card.error": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          ERROR: {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
        },
      },
      backsidePending: {
        on: {
          ERROR: {
            target: "error",
            actions: [
              assign({
                scannerRunning: true,
                processing: false,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
          "card.error": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          "card.backside_failed": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          TOGGLE: [
            {
              //when toggled from manual to auto scan, transition immediately
              target: "backsideProcessing",
              cond: (ctx) => !ctx.autoScan,
              actions: [
                assign({
                  scannerRunning: true,
                  processing: true,
                  autoScan: true,
                  toggledAutoManual: true,
                }),
              ],
            },
            {
              //when toggled from auto to manual scan, disable runner and await manual button click
              cond: (ctx) => ctx.autoScan,
              actions: [
                assign({
                  scannerRunning: false,
                  processing: false,
                  autoScan: false,
                  toggledAutoManual: true,
                }),
              ],
            },
          ],
          MANUAL_BUTTON_CLICK: {
            target: "backsideProcessing",
            actions: [
              assign({
                scannerRunning: true,
                processing: true,
              }),
            ],
          },
        },
        after: {
          // after 5 seconds, transition to backsideProcessing
          5000: {
            target: "backsideProcessing",
            cond: (ctx) => ctx.autoScan,
            actions: [
              assign({
                autoScan: true,
                scannerRunning: true,
              }),
            ],
          },
        },
      },
      postProcessing: {
        on: {
          "card.completed": [
            {
              target: "completed",
              actions: [
                assign({
                  scannerRunning: false,
                }),
              ],
            },
          ],
          "card.error": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          ERROR: {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
        },
      },
      backsideProcessing: {
        on: {
          MANUAL_BUTTON_CLICK: {
            actions: [
              assign({
                scannerRunning: true,
              }),
            ],
          },
          "scan.processing": {
            actions: [
              assign({
                scannerRunning: true,
                processing: true,
              }),
            ],
          },
          "card.post_processing": [
            {
              target: "postProcessing",
              actions: [
                assign({
                  scannerRunning: false,
                  processing: true,
                }),
              ],
            },
          ],
          "card.completed": {
            target: "completed",
            actions: [
              assign({
                scannerRunning: false, //replaces on cleanUp
              }),
            ],
          },
          "card.backside_failed": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          "card.error": {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false,
              }),
            ],
          },
          ERROR: {
            target: "retry",
            actions: [
              assign({
                scannerRunning: false, //replaces on cleanUp
                httpCode: (context, event) => event.httpCode,
                httpMessage: (context, event) => event.httpMessage,
              }),
            ],
          },
        },
      },
      retry: {
        on: {
          retry_scan: {
            target: "autoCapture",
            actions: [
              assign({
                scannerRunning: true,
                autoScan: true,
                processing: false,
              }),
            ],
          },
          "card.completed": {
            target: "completed",
            actions: [
              assign({
                card_id: (context, event) => event.card_id,
              }),
            ],
          },
        },
      },
      completed: {
        always: [
          {
            cond: (context) => context.eligibilitySupport === true,
            target: "eligibilityPending",
          },
          { target: "success" },
        ],
      },
      eligibilityPending: {
        on: {
          "eligibility.processing": {
            target: "eligibilityProcessing",
          },
        },
      },
      eligibilityProcessing: {
        on: {
          "eligibility.completed": {
            target: "success",
          },
          "eligibility.error": {
            target: "success",
          },
        },
      },
      eligibilityError: {
        on: {
          "*": {
            actions: [],
          },
        },
      },
      success: {
        type: "final",
      },
      error: {
        //type: "final",
        on: {
          "card.completed": {
            target: "completed",
            actions: [
              assign({
                scannerRunning: false, //replaces on cleanUp
                processing: true,
              }),
            ],
          },
          retryCamera: {
            target: "cameraLoading",
          },
        },
      },
      cameraError: {
        //type: "final",
        on: {
          "card.completed": {
            target: "completed",
            actions: [
              assign({
                scannerRunning: false, //replaces on cleanUp
                processing: true,
              }),
            ],
          },
          retryCamera: {
            target: "cameraLoading",
          },
        },
      },
    },
  },
  {
    actions: {
      log: (context, event, meta) => {
        GlobalLogger.debug("backSideSupport: " + context.backSideSupport);
      },
    },
  }
);

export type ScanViewContext = {
  card_id: string;
  scan_id: string;
  autoScan: boolean;
  scannerRunning: boolean;
  backSideSupport: boolean;
  eligibilitySupport: boolean;
  headerTitle: any;
  messageState: Messages;
  toggledAutoManual: boolean;
  processing: boolean;
  error?: string;
  httpCode: number;
  httpMessage: string;
  session_id: string;
  modelState: string;
};

export type ScanViewEvent =
  | { type: "CAMERA_LOADED" }
  | { type: "retryCamera" }
  | { type: "UPDATE_MODEL_STATE"; modelState: string }
  | { type: "TOGGLE" }
  | { type: "MANUAL_BUTTON_CLICK" }
  | { type: "ERROR"; value: Error; httpCode: number; httpMessage: string }
  | { type: "CAMERAERROR"; value: Error; httpCode: number; httpMessage: string }
  | { type: "CARD_GAP_DETECTED" }
  | {
      type: "scan.pending";
      card_id: string;
      scan_id: string;
      backSideSupport: boolean;
      orientation: string;
    }
  | {
      type: "scan.processing";
      card_id: string;
      scan_id: string;
      backSideSupport: boolean;
      orientation: string;
    }
  | { type: "retry_scan" }
  | { type: "scan.completed"; card_id: string; scan_id: string }
  | { type: "scan.unknown"; card_id: string; scan_id: string }
  | { type: "scan.skipped"; card_id: string; scan_id: string }
  | { type: "scan.error"; card_id: string; scan_id: string }
  | { type: "scan.rejected"; card_id: string; scan_id: string }
  | { type: "card.pending"; card_id: string; scan_id: string }
  | { type: "card.processing"; card_id: string; scan_id: string }
  | {
      type: "card.frontside_processing";
      card_id: string;
      scan_id: string;
      backSideSupport: boolean;
      eligibilitySupport: boolean;
      session_id: string;
    }
  | { type: "card.frontside_completed"; card_id: string; scan_id: string }
  | { type: "card.post_processing"; card_id: string; scan_id: string }
  | { type: "card.frontside_failed"; card_id: string; scan_id: string }
  | { type: "card.backside_processing"; card_id: string; scan_id: string }
  | { type: "card.backside_failed"; card_id: string; scan_id: string }
  | { type: "card.completed"; card_id: string; scan_id: string }
  | { type: "card.unknown"; card_id: string; scan_id: string }
  | { type: "card.error"; card_id: string; scan_id: string }
  | { type: "eligibility.processing"; card_id: string; eligibility_id: string }
  | { type: "eligibility.error"; card_id: string; scan_id: string }
  | { type: "eligibility.completed"; card_id: string; scan_id: string }
  | { type: "eligibilityError" };

export default scanViewStateMachine;
